// DTF Builder. Rolling 57 cm wide canvas with adjustable length per sheet,
// drag-to-place artwork with live DPI feedback, multi-sheet management with
// per-sheet quantity, and length-based pricing. Submit creates an order via
// /api/orders with a dtfSheets[] body (orders.ts already handles that branch).
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { fabric } from 'fabric'
import TopBar from '../components/TopBar'
import LibraryDrawer, { SavedDesign, SavedUpload } from '../components/LibraryDrawer'
import TextToolbar from '../components/TextToolbar'
import AlignmentToolbar from '../components/AlignmentToolbar'
import InlineUploadLibrary, { InlineUploadLibraryHandle, UploadAsset } from '../components/InlineUploadLibrary'
import StudioMediaPicker, { StudioAsset } from '../components/StudioMediaPicker'
import UploadConsentModal, { PendingUpload, computeUploadWarnings, hasSessionConsent, rememberSessionConsent } from '../components/UploadConsentModal'
import { apiFetch } from '../lib/api'
import { bakeDtfSheetThumbnail, uploadBakedThumbnail } from '../lib/bake-piece-thumbnail'
import EditorWizardBar from './admin/EditorWizardBar'
import WizardStepModal from '../components/WizardStepModal'

// ── Catalog response types (subset) ────────────────────────────────────
import type { CatProduct, Catalog } from '../lib/catalog-types'
interface DesignRow { id: string }
interface FileResp { id: string; url: string }

// Per-sheet state — the array of these is saved in designs.spec.
interface SheetState {
  id: string
  name: string
  lengthCm: number
  qty: number
  fabricJson: object | null
}

interface DtfSpec {
  kind: 'dtf'
  sheets: SheetState[]
  activeSheetId: string
  marginCm: number
  paddingCm: number
}

const SHEET_WIDTH_CM = 57
const PX_PER_CM = 10
const SHEET_WIDTH_PX = SHEET_WIDTH_CM * PX_PER_CM // 570
const DEFAULT_LENGTH_CM = 100
const MIN_LENGTH_CM = 30
const MAX_LENGTH_CM = 300

let sheetCounter = 0
function newSheet(): SheetState {
  sheetCounter++
  return {
    id: Math.random().toString(36).slice(2, 10),
    name: `Sheet ${sheetCounter}`,
    lengthCm: DEFAULT_LENGTH_CM,
    qty: 1,
    fabricJson: null,
  }
}

// DPI quality bands.
function dpiQuality(ppi: number): { label: string; bg: string; fg: string } {
  if (ppi >= 250) return { label: 'Optimal', bg: 'bg-emerald-100', fg: 'text-emerald-800' }
  if (ppi >= 200) return { label: 'Good',    bg: 'bg-amber-100',   fg: 'text-amber-800' }
  return { label: 'Poor', bg: 'bg-red-100', fg: 'text-red-700' }
}

type Tool = 'images' | 'text' | 'settings'

export default function DTF() {
  const navigate = useNavigate()
  const { user, getAccessToken } = useAuth()

  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sheets, setSheets] = useState<SheetState[]>(() => { sheetCounter = 0; return [newSheet()] })
  const [activeId, setActiveId] = useState<string>(() => sheets[0]!.id)
  const [marginCm, setMarginCm] = useState(0)
  const [paddingCm, setPaddingCm] = useState(0.5)
  const [designId, setDesignId] = useState<string | null>(null)
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [tool, setTool] = useState<Tool>('images')
  // Wizard-modal state for the brand-red step bar. Null = closed.
  const [wizModalStep, setWizModalStep] = useState<string | null>(null)
  // Preview-only substrate colour. The DTF transfer prints on clear film
  // so the saved spec stays transparent — this toggle just changes the
  // CSS backdrop behind the canvas so the customer can see their design
  // on white / grey / black garments as they build.
  const [sheetBg, setSheetBg] = useState<'white' | 'grey' | 'black'>('white')
  const [selection, setSelection] = useState<{ name?: string; ppi?: number; widthCm?: number; heightCm?: number } | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [studioMediaOpen, setStudioMediaOpen] = useState(false)
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null)
  const [pendingWarnings, setPendingWarnings] = useState<string[]>([])
  const [canvasTick, setCanvasTick] = useState(0)
  const bump = () => setCanvasTick((x) => x + 1)

  const canvasElRef = useRef<HTMLCanvasElement | null>(null)
  const fabricRef = useRef<fabric.Canvas | null>(null)
  const switchingRef = useRef(false) // suppress dirty flips while we swap sheets
  const libraryRef = useRef<InlineUploadLibraryHandle | null>(null)
  // Mirror of `sheets` that's always up-to-date — closure-safe access for the
  // switch/add/remove helpers, which run from callbacks where React's
  // batched state hasn't applied yet.
  const sheetsRef = useRef<SheetState[]>(sheets)
  // Dirty flag — set true by canvas events when the user makes a change,
  // reset by persist() when the explicit Save completes. Drives both the
  // Save-button highlight and the "unsaved changes" prompt on sheet switch.
  const [dirty, setDirty] = useState(false)

  const [submit, setSubmit] = useState<{ open: boolean; proof?: string; proofFileId?: string; busy: boolean }>({ open: false, busy: false })
  const [shipping, setShipping] = useState({
    fullName: '', email: user?.email ?? '', phone: '', line1: '', line2: '',
    city: '', region: '', postcode: '', country: 'AU',
  })
  // Pre-populate from the signed-in customer's saved shipping (or, if no
  // explicit profile row, the last order's shipping snapshot — /api/me
  // does that fallback for us). Hits /api/me directly so the prefill
  // works even when AuthKit React's `user` hasn't hydrated yet but the
  // server-side sealed cookie is valid.
  const shippingHydrated = useRef(false)
  useEffect(() => {
    if (shippingHydrated.current) return
    let cancelled = false
    ;(async () => {
      try {
        type MeResp = {
          authenticated: boolean
          user?: { email: string } | null
          shipping?: { fullName: string; phone: string | null; line1: string; line2: string | null; city: string; region: string | null; postcode: string; country: string } | null
        }
        const m = await apiFetch<MeResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/me')
        if (cancelled || !m?.authenticated) return
        shippingHydrated.current = true
        const s = m.shipping
        const userEmail = m.user?.email ?? ''
        setShipping((prev) => ({
          fullName: prev.fullName || s?.fullName || '',
          email:    prev.email    || userEmail,
          phone:    prev.phone    || (s?.phone ?? ''),
          line1:    prev.line1    || s?.line1 || '',
          line2:    prev.line2    || (s?.line2 ?? ''),
          city:     prev.city     || s?.city || '',
          region:   prev.region   || (s?.region ?? ''),
          postcode: prev.postcode || s?.postcode || '',
          country:  prev.country  || s?.country || 'AU',
        }))
      } catch { /* signed out / no profile / network — keep blanks */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load catalog
  useEffect(() => {
    (async () => {
      try {
        const c = await apiFetch<Catalog>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/catalog')
        setCatalog(c)
      } catch (e) { setError((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dtfProduct = useMemo(() => catalog?.products.find((p) => p.kind === 'dtf'), [catalog])
  const activeSheet = sheets.find((s) => s.id === activeId) ?? sheets[0]!

  // Init Fabric canvas once
  useEffect(() => {
    if (!canvasElRef.current || fabricRef.current) return
    const c = new fabric.Canvas(canvasElRef.current, {
      width: SHEET_WIDTH_PX, height: activeSheet.lengthCm * PX_PER_CM,
      selection: true, preserveObjectStacking: true,
      // Transparent canvas surface — the press file MUST carry alpha so
      // the printer can drive the white-ink underlay. Substrate colour
      // the customer sees during design comes from the wrapper div's CSS
      // background (`sheetBg`), not from the fabric canvas itself.
      backgroundColor: '',
    })
    const onSel = () => {
      const a = c.getActiveObject()
      if (a && a.type === 'image') {
        const img = a as fabric.Image
        const el = img.getElement() as HTMLImageElement
        const naturalW = el.naturalWidth || 0
        const widthCm = img.getScaledWidth() / PX_PER_CM
        const heightCm = img.getScaledHeight() / PX_PER_CM
        const ppi = naturalW > 0 ? (naturalW / Math.max(widthCm, 0.001)) * 2.54 : 0
        setSelection({
          name: ((img as fabric.Object & { data?: { name?: string } }).data?.name) ?? 'Image',
          ppi: Math.round(ppi),
          widthCm: +widthCm.toFixed(1),
          heightCm: +heightCm.toFixed(1),
        })
      } else { setSelection(null) }
    }
    c.on('selection:created', () => { onSel(); bump() })
    c.on('selection:updated', () => { onSel(); bump() })
    c.on('selection:cleared', () => { setSelection(null); bump() })
    c.on('object:scaling',    () => { onSel(); bump() })
    c.on('object:moving',     () => bump())
    c.on('object:modified',   () => { onSel(); if (!switchingRef.current) setDirty(true); bump() })
    c.on('object:added',      () => { if (!switchingRef.current) setDirty(true); bump() })
    c.on('object:removed',    () => { if (!switchingRef.current) setDirty(true); bump() })
    fabricRef.current = c
    return () => { c.dispose(); fabricRef.current = null }
    // Depend on `catalog` so we re-try init once the loading-state render swaps
    // out and the <canvas> element finally enters the DOM. The guard above
    // (`if (... || fabricRef.current) return`) keeps it idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog])

  // Resize canvas when active sheet length changes
  useEffect(() => {
    const c = fabricRef.current; if (!c) return
    c.setHeight(activeSheet.lengthCm * PX_PER_CM)
    c.renderAll()
  }, [activeSheet.lengthCm])

  // Mirror `sheets` into a ref so switch/add/remove helpers can read the
  // latest state without closure issues (their callers run via microtask,
  // before React applies the corresponding setSheets).
  useEffect(() => { sheetsRef.current = sheets }, [sheets])

  // Internal switch (no prompt). Always reads the LATEST sheets from the ref
  // so the snapshot persists even when called moments after addSheet.
  const switchToImmediate = useCallback((targetId: string) => {
    const c = fabricRef.current; if (!c || targetId === activeId) return
    switchingRef.current = true
    // Snapshot the current canvas into the current active sheet (in the ref).
    const snapshot = c.toJSON()
    const next = sheetsRef.current.map((s) => s.id === activeId ? { ...s, fabricJson: snapshot } : s)
    sheetsRef.current = next
    setSheets(next)
    // Load the target's saved canvas (if any).
    const target = next.find((s) => s.id === targetId)
    c.clear(); c.backgroundColor = '#ffffff'
    if (target?.fabricJson) {
      c.loadFromJSON(target.fabricJson, () => {
        c.renderAll()
        window.setTimeout(() => { switchingRef.current = false; setDirty(false) }, 50)
      })
    } else {
      c.renderAll()
      window.setTimeout(() => { switchingRef.current = false; setDirty(false) }, 50)
    }
    setActiveId(targetId); setSelection(null)
  }, [activeId])

  // Public switch — prompts the user when there are unsaved edits.
  const switchTo = useCallback((targetId: string) => {
    if (targetId === activeId) return
    if (dirty) {
      const ok = window.confirm('You have unsaved changes on this sheet. Save them before switching?')
      if (!ok) return
    }
    switchToImmediate(targetId)
  }, [activeId, dirty, switchToImmediate])

  // Per-piece flat-PNG thumbnails uploaded at submit time. Keyed by sheet
  // INDEX ('sheet:0', 'sheet:1' …) to mirror the API's piece keying.
  const pieceThumbnailsRef = useRef<Record<string, string>>({})

  // Persistence — explicit Save (no longer debounced)
  const buildSpec = useCallback((): DtfSpec & { pieceThumbnails?: Record<string, string> } => {
    const c = fabricRef.current
    const snapshot = sheets.map((s) => (s.id === activeId && c) ? { ...s, fabricJson: c.toJSON() } : s)
    return {
      kind: 'dtf', sheets: snapshot, activeSheetId: activeId, marginCm, paddingCm,
      // Per-piece thumbnails (sheet:<idx> -> fileId). Populated by openSubmit;
      // empty for drafts.
      pieceThumbnails: { ...pieceThumbnailsRef.current },
    }
  }, [sheets, activeId, marginCm, paddingCm])

  const persist = useCallback(async () => {
    if (switchingRef.current) return
    setSaving('saving')
    try {
      const spec = buildSpec()
      if (!designId) {
        const row = await apiFetch<DesignRow>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/designs', {
          method: 'POST', body: JSON.stringify({ kind: 'dtf', spec }),
        })
        setDesignId(row.id)
      } else {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/designs/${designId}`, {
          method: 'PATCH', body: JSON.stringify({ spec }),
        })
      }
      setSaving('saved')
      setDirty(false)
    } catch { setSaving('error') }
  }, [buildSpec, designId, getAccessToken])

  // Auto-save was removed — it created a new library row on every nudge,
  // piling up dozens of near-identical drafts. Sheets, active sheet, and
  // margins still flip the dirty flag so the explicit Save button surfaces.
  useEffect(() => { setDirty(true) /* eslint-disable-next-line */ }, [sheets, activeId, marginCm, paddingCm])

  // Image upload — same two-step pattern as Designer (instant local preview,
  // swap to signed-URL source once the upload returns)
  // Stage 1 — probe + gate
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return
    e.target.value = ''
    if (f.size > 25 * 1024 * 1024) { alert('Max 25 MB per image'); return }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      const probe = new Image()
      probe.onload = () => {
        const pending: PendingUpload = { file: f, dataUrl, naturalWidth: probe.naturalWidth, naturalHeight: probe.naturalHeight }
        const warnings = computeUploadWarnings(pending)
        if (hasSessionConsent() && warnings.length === 0) {
          void doUpload(pending)
        } else {
          setPendingUpload(pending); setPendingWarnings(warnings)
        }
      }
      probe.src = dataUrl
    }
    reader.readAsDataURL(f)
  }

  // Stage 2 — proceed past the consent/warnings modal.
  async function doUpload(p: PendingUpload) {
    const c = fabricRef.current; if (!c) return
    fabric.Image.fromURL(p.dataUrl, (img) => {
      // True-to-size preview: convert the upload's intrinsic px dimensions to
      // its physical print size at 300 DPI (the studio preferred ceiling), then
      // map that physical size into canvas px via PX_PER_CM. Earlier rev hard-
      // coded every upload to 30 % of sheet width, which was never the actual
      // print size and confused customers (a 30 cm logo and a 5 cm logo both
      // landed the same size on screen).
      const TARGET_DPI = 300
      const PX_PER_INCH = TARGET_DPI
      const trueWidthCm  = (p.naturalWidth  / PX_PER_INCH) * 2.54
      const trueHeightCm = (p.naturalHeight / PX_PER_INCH) * 2.54
      const targetCanvasW = trueWidthCm  * PX_PER_CM
      const targetCanvasH = trueHeightCm * PX_PER_CM
      // Cap to the usable sheet area so a poster-sized upload doesn't spill
      // off-canvas on first drop; the customer can still scale up afterwards.
      const usableW = SHEET_WIDTH_PX - marginCm * PX_PER_CM * 2 - 20
      const usableH = activeSheet.lengthCm * PX_PER_CM - marginCm * PX_PER_CM * 2 - 20
      const scaleCap = Math.min(usableW / targetCanvasW, usableH / targetCanvasH, 1)
      const finalCanvasW = Math.max(30, targetCanvasW * scaleCap)
      img.scaleToWidth(finalCanvasW)
      img.set({
        left: Math.max(10, marginCm * PX_PER_CM) + 10,
        top:  Math.max(10, marginCm * PX_PER_CM) + 10,
      })
      ;(img as fabric.Object & { data?: { tempLocal?: boolean; fileId?: string; name?: string } }).data = { tempLocal: true, name: p.file.name }
      c.add(img); c.setActiveObject(img); c.requestRenderAll()
    })
    try {
      const up = await apiFetch<FileResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
        method: 'POST',
        body: JSON.stringify({ originalName: p.file.name, mimeType: p.file.type || 'application/octet-stream', base64: p.dataUrl, kind: 'artwork', widthPx: p.naturalWidth, heightPx: p.naturalHeight }),
      })
      const active = c.getActiveObject() as (fabric.Image & { data?: { tempLocal?: boolean; fileId?: string; name?: string } }) | null
      if (active && active.type === 'image' && active.data?.tempLocal) {
        active.setSrc(up.url, () => {
          active.data = { tempLocal: false, fileId: up.id, name: p.file.name }
          c.requestRenderAll(); setDirty(true)
        })
      }
      libraryRef.current?.addUpload({
        id: up.id, originalName: p.file.name, url: up.url,
        widthPx: p.naturalWidth, heightPx: p.naturalHeight,
        createdAt: new Date().toISOString(),
      })
    } catch (err) { console.warn('upload failed:', (err as Error).message) }
  }

  function addText() {
    const c = fabricRef.current; if (!c) return
    const text = new fabric.IText('Your Text', {
      left: SHEET_WIDTH_PX * 0.2, top: 60,
      fontFamily: 'Barlow Condensed', fontSize: 64, fontWeight: 'bold', fill: '#0F1117',
    })
    c.add(text); c.setActiveObject(text); c.requestRenderAll()
  }

  // Reopen a saved DTF design — restores sheets + canvases.
  async function openSavedDesign(d: SavedDesign) {
    const c = fabricRef.current; if (!c) return
    try {
      const row = await apiFetch<{ item: { id: string; spec: unknown } }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/library/designs/${d.id}`)
      const spec = row.item.spec as DtfSpec | null
      if (!spec?.sheets?.length) return
      sheetCounter = spec.sheets.length
      setSheets(spec.sheets)
      setActiveId(spec.activeSheetId ?? spec.sheets[0]!.id)
      if (typeof spec.marginCm === 'number') setMarginCm(spec.marginCm)
      if (typeof spec.paddingCm === 'number') setPaddingCm(spec.paddingCm)
      setDesignId(d.id)
      const target = spec.sheets.find((s) => s.id === (spec.activeSheetId ?? spec.sheets[0]!.id)) ?? spec.sheets[0]!
      c.clear(); c.backgroundColor = '#ffffff'
      if (target.fabricJson) c.loadFromJSON(target.fabricJson, () => c.renderAll())
      setLibraryOpen(false)
    } catch (e) { alert((e as Error).message) }
  }

  function useUpload(u: SavedUpload) {
    const c = fabricRef.current; if (!c) return
    fabric.Image.fromURL(u.url, (img) => {
      // True-to-size preview at 300 DPI (matches doUpload). Library + studio
      // picks carry the original widthPx/heightPx so we can compute the
      // intended physical print size and map it onto the sheet canvas.
      const TARGET_DPI = 300
      const wPx = u.widthPx  ?? img.width  ?? 0
      const hPx = u.heightPx ?? img.height ?? 0
      let finalCanvasW = Math.max(80, SHEET_WIDTH_PX * 0.3) // fallback for missing dims
      if (wPx > 0 && hPx > 0) {
        const trueWidthCm   = (wPx / TARGET_DPI) * 2.54
        const trueHeightCm  = (hPx / TARGET_DPI) * 2.54
        const targetCanvasW = trueWidthCm  * PX_PER_CM
        const targetCanvasH = trueHeightCm * PX_PER_CM
        const usableW = SHEET_WIDTH_PX - marginCm * PX_PER_CM * 2 - 20
        const usableH = activeSheet.lengthCm * PX_PER_CM - marginCm * PX_PER_CM * 2 - 20
        const scaleCap = Math.min(usableW / targetCanvasW, usableH / targetCanvasH, 1)
        finalCanvasW = Math.max(30, targetCanvasW * scaleCap)
      }
      img.scaleToWidth(finalCanvasW)
      img.set({ left: 20, top: 20 })
      ;(img as fabric.Object & { data?: { fileId?: string; name?: string; reused?: boolean } }).data = { fileId: u.id, name: u.originalName, reused: true }
      c.add(img); c.setActiveObject(img); c.requestRenderAll()
    }, { crossOrigin: 'anonymous' })
    setLibraryOpen(false)
  }

  function deleteSelected() {
    const c = fabricRef.current; if (!c) return
    const a = c.getActiveObject(); if (!a) return
    // Multi-select: c.remove(activeSelection) just drops the wrapper, so
    // pull each contained object out and remove them individually.
    if (a.type === 'activeSelection') {
      const grp = a as fabric.ActiveSelection
      const items = grp.getObjects().slice()
      grp.remove(...items)
      c.discardActiveObject()
      items.forEach((o) => c.remove(o))
    } else {
      c.remove(a)
    }
    c.requestRenderAll(); setSelection(null)
  }

  // Sheet operations — all use sheetsRef so the snapshot persists even when
  // the next render hasn't applied yet.
  function addSheet() {
    const c = fabricRef.current; if (!c) return
    if (dirty) {
      const ok = window.confirm('You have unsaved changes on this sheet. Save them before creating a new sheet?')
      if (!ok) return
    }
    switchingRef.current = true
    const snapshot = c.toJSON()
    const next = newSheet()
    const updated = sheetsRef.current.map((s) => s.id === activeId ? { ...s, fabricJson: snapshot } : s).concat(next)
    sheetsRef.current = updated
    setSheets(updated)
    setActiveId(next.id); setSelection(null)
    c.clear(); c.backgroundColor = '#ffffff'; c.renderAll()
    window.setTimeout(() => { switchingRef.current = false; setDirty(false) }, 50)
  }

  function duplicateSheet(id: string) {
    const c = fabricRef.current; if (!c) return
    // Snapshot the live canvas if duplicating the active sheet — otherwise
    // the duplicate would miss edits since the last switch/save.
    const snapshot = c.toJSON()
    const baseline = sheetsRef.current.map((s) => s.id === activeId ? { ...s, fabricJson: snapshot } : s)
    const src = baseline.find((s) => s.id === id); if (!src) return
    const copy: SheetState = {
      ...newSheet(),
      lengthCm: src.lengthCm, qty: src.qty,
      fabricJson: src.fabricJson ? JSON.parse(JSON.stringify(src.fabricJson)) as object : null,
    }
    const updated = [...baseline, copy]
    sheetsRef.current = updated
    setSheets(updated)
  }

  function removeSheet(id: string) {
    if (sheetsRef.current.length <= 1) return
    const filtered = sheetsRef.current.filter((s) => s.id !== id)
    sheetsRef.current = filtered
    setSheets(filtered)
    if (id === activeId) {
      // Switch to the first remaining sheet directly via the immediate helper.
      const first = filtered[0]
      if (first) switchToImmediate(first.id)
    }
  }

  function updateActiveSheet(patch: Partial<SheetState>) {
    setSheets((prev) => prev.map((s) => s.id === activeId ? { ...s, ...patch } : s))
  }

  // ─── Placement helpers — used by the wizard's "Artwork placement"
  // step. The DTF "print area" is the whole active sheet (57 cm × N cm).
  function dtfUserObjects(): fabric.Object[] {
    const c = fabricRef.current
    if (!c) return []
    return c.getObjects().filter((o) => !(o as fabric.Object & { __guide?: boolean }).__guide)
  }
  function dtfUserBoundingBox(): { left: number; top: number; width: number; height: number } | null {
    const objs = dtfUserObjects()
    if (objs.length === 0) return null
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    for (const o of objs) {
      const b = o.getBoundingRect(true, true)
      minX = Math.min(minX, b.left); maxX = Math.max(maxX, b.left + b.width)
      minY = Math.min(minY, b.top);  maxY = Math.max(maxY, b.top  + b.height)
    }
    return { left: minX, top: minY, width: maxX - minX, height: maxY - minY }
  }
  function dtfSheetZone() {
    return { left: 0, top: 0, width: SHEET_WIDTH_PX, height: activeSheet.lengthCm * PX_PER_CM }
  }
  function dtfPlacementCenter() {
    const c = fabricRef.current; if (!c) return
    const bbox = dtfUserBoundingBox(); if (!bbox) return
    const z = dtfSheetZone()
    const dx = (z.left + z.width / 2) - (bbox.left + bbox.width / 2)
    const dy = (z.top  + z.height / 2) - (bbox.top  + bbox.height / 2)
    for (const o of dtfUserObjects()) {
      o.set({ left: (o.left ?? 0) + dx, top: (o.top ?? 0) + dy })
      o.setCoords()
    }
    c.requestRenderAll(); setDirty(true)
  }
  function dtfPlacementScale(factor: number) {
    const c = fabricRef.current; if (!c) return
    const bbox = dtfUserBoundingBox(); if (!bbox) return
    const cx = bbox.left + bbox.width / 2
    const cy = bbox.top  + bbox.height / 2
    for (const o of dtfUserObjects()) {
      o.set({
        scaleX: (o.scaleX ?? 1) * factor,
        scaleY: (o.scaleY ?? 1) * factor,
        left: cx + ((o.left ?? 0) - cx) * factor,
        top:  cy + ((o.top  ?? 0) - cy) * factor,
      })
      o.setCoords()
    }
    c.requestRenderAll(); setDirty(true)
  }
  function dtfPlacementFit(mode: 'width' | 'height' | 'contain') {
    const c = fabricRef.current; if (!c) return
    const bbox = dtfUserBoundingBox(); if (!bbox) return
    const z = dtfSheetZone()
    const sx = z.width  / bbox.width
    const sy = z.height / bbox.height
    const factor = mode === 'width' ? sx : mode === 'height' ? sy : Math.min(sx, sy)
    const newW = bbox.width  * factor
    const newH = bbox.height * factor
    const targetLeft = z.left + (z.width  - newW) / 2
    const targetTop  = z.top  + (z.height - newH) / 2
    for (const o of dtfUserObjects()) {
      const relX = (o.left ?? 0) - bbox.left
      const relY = (o.top  ?? 0) - bbox.top
      o.set({
        scaleX: (o.scaleX ?? 1) * factor,
        scaleY: (o.scaleY ?? 1) * factor,
        left: targetLeft + relX * factor,
        top:  targetTop  + relY * factor,
      })
      o.setCoords()
    }
    c.requestRenderAll(); setDirty(true)
  }

  // Totals
  const totals = useMemo(() => {
    // Mirror the server exactly: it rounds pricePerCm to whole cents first
    // (Math.round(pricePerCm * 100)) then multiplies, so the displayed total
    // matches the amount actually charged for any non-2dp pricePerCm.
    const perCmCents = Math.round((dtfProduct?.pricePerCm ?? 0.28) * 100)
    let totalCm = 0, totalQty = 0, totalCents = 0
    for (const s of sheets) {
      totalCm += s.lengthCm * s.qty
      totalQty += s.qty
      totalCents += perCmCents * s.lengthCm * s.qty
    }
    return { totalCm, totalQty, total: totalCents / 100 }
  }, [sheets, dtfProduct])

  // Submit
  async function openSubmit() {
    const c = fabricRef.current; if (!c) return
    // Snapshot current sheet first so the preview reflects latest edits.
    // We compute the post-snapshot sheets locally too so we can bake
    // thumbnails before the React state has flushed.
    const liveJson = c.toJSON()
    const snapshot = sheets.map((s) => s.id === activeId ? { ...s, fabricJson: liveJson as object } : s)
    setSheets(snapshot)

    const proof = c.toDataURL({ format: 'png', multiplier: 2 })
    setSubmit({ open: true, proof, busy: false })
    try {
      const up = await apiFetch<FileResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/files', {
        method: 'POST',
        body: JSON.stringify({ originalName: 'dtf-proof.png', mimeType: 'image/png', base64: proof, kind: 'proof' }),
      })
      setSubmit((s) => ({ ...s, proofFileId: up.id }))
    } catch { /* user can still submit without proof reference */ }

    // Bake one flat PNG per sheet that has actual fabric content, upload
    // each as kind='mockup', and stash the resulting fileIds keyed by the
    // SHEET INDEX (matches the API's `sheet:<i>` piece keys).
    const thumbMap: Record<string, string> = {}
    await Promise.all(snapshot.map(async (s, i) => {
      if (!s.fabricJson) return
      try {
        const dataUrl = await bakeDtfSheetThumbnail(s.fabricJson, s.lengthCm)
        if (!dataUrl) return
        const fileId = await uploadBakedThumbnail(dataUrl, `mockup-sheet-${i + 1}.png`)
        thumbMap[`sheet:${i}`] = fileId
      } catch { /* one sheet failing shouldn't block submit */ }
    }))
    pieceThumbnailsRef.current = thumbMap
    // Persist (PATCH or POST) so the spec carries pieceThumbnails before
    // the customer clicks Place order.
    await persist()
  }

  async function placeOrder() {
    if (!dtfProduct) return
    setSubmit((s) => ({ ...s, busy: true }))
    try {
      // Mirror Designer: persist the design if the user never explicitly
      // pressed Save (auto-save was removed). Without this the order POST
      // would 400 on a null designId and the button looks like a no-op.
      let did = designId
      if (!did) {
        const spec = buildSpec()
        const row = await apiFetch<DesignRow>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/designs', {
          method: 'POST', body: JSON.stringify({ kind: 'dtf', spec }),
        })
        did = row.id
        setDesignId(row.id); setDirty(false)
      }
      const res = await apiFetch<{ id: string }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          designId: did, productId: dtfProduct.id,
          dtfSheets: sheets.map((s) => ({ lengthCm: s.lengthCm, qty: s.qty })),
          proofFileId: submit.proofFileId ?? null,
          shipping,
        }),
      })
      navigate(`/orders/${res.id}`)
    } catch (e) {
      alert(`Could not place order: ${(e as Error).message}`)
      setSubmit((s) => ({ ...s, busy: false }))
    }
  }

  if (error) return <div className="min-h-screen flex flex-col"><TopBar /><div className="p-10 text-ink-3">Error: {error}</div></div>
  if (!catalog || !dtfProduct) return <div className="min-h-screen flex flex-col"><TopBar title="DTF Builder" /><div className="flex-1 flex items-center justify-center text-ink-3 text-sm">Loading…</div></div>

  // Has any user image landed on the current sheet's fabric canvas?
  // (Background grid / margin guides don't count.)
  const hasArtwork = (() => {
    const c = fabricRef.current
    if (!c) return false
    return c.getObjects().some((o) => !(o as fabric.Object & { __guide?: boolean }).__guide)
  })()
  const wizardStepOrder = ['upload', 'placement', 'sheet', 'qty'] as const
  type WizStep = typeof wizardStepOrder[number]
  const wizardSteps = [
    { id: 'upload',    label: 'Add artwork',       done: hasArtwork,                   onClick: () => setWizModalStep('upload') },
    { id: 'placement', label: 'Artwork placement', done: hasArtwork,                   onClick: () => setWizModalStep('placement') },
    { id: 'sheet',     label: 'Sheet length',      done: activeSheet.lengthCm > 0,     onClick: () => setWizModalStep('sheet') },
    { id: 'qty',       label: 'Quantity',          done: sheets.some(s => s.qty > 0),  onClick: () => setWizModalStep('qty') },
    { id: 'submit',    label: 'Submit',            onClick: openSubmit },
  ]
  function gotoWizStep(dir: 'next' | 'prev') {
    if (!wizModalStep) return
    const i = wizardStepOrder.indexOf(wizModalStep as WizStep)
    if (i === -1) return
    if (dir === 'next') {
      if (i === wizardStepOrder.length - 1) { setWizModalStep(null); openSubmit(); return }
      setWizModalStep(wizardStepOrder[i + 1])
    } else {
      if (i === 0) { setWizModalStep(null); return }
      setWizModalStep(wizardStepOrder[i - 1])
    }
  }

  return (
    <div className="h-screen flex flex-col bg-surface overflow-hidden">
      <TopBar
        title="DTF Builder"
        right={
          <div className="flex items-center gap-3 text-xs">
            {user && (
              <button onClick={() => void persist()} disabled={!dirty || saving === 'saving'}
                className={'btn-ghost-dark ' + (dirty ? 'text-gold' : 'text-white/45 cursor-default')}
                title={dirty ? 'Save current design to your library' : 'No unsaved changes'}>
                {saving === 'saving' ? 'Saving…' : saving === 'error' ? 'Retry save' : dirty ? 'Save' : 'Saved'}
              </button>
            )}
            <button onClick={() => setLibraryOpen(true)} className="btn-ghost-dark">My Library</button>
            <div className="hidden sm:flex items-center gap-2 text-white/70"><span className="text-white/45">Sheets</span><span className="font-semibold text-white">{sheets.length}</span></div>
            <div className="flex items-center gap-2 text-white/70"><span className="text-white/45">$</span><span className="font-semibold text-gold text-base">{totals.total.toFixed(2)}</span></div>
            <button onClick={openSubmit} className="btn-gold">Submit</button>
          </div>
        }
      />

      <EditorWizardBar
        title="DTF Builder"
        variant="inline"
        steps={wizardSteps}
      />

      <div className="flex flex-1 min-h-0">
        {/* LEFT TOOLBAR */}
        <nav className="bg-ink w-[78px] flex-shrink-0 border-r border-white/5 flex flex-col py-2">
          <ToolBtn id="images" label="Images" tool={tool} setTool={setTool}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          </ToolBtn>
          <ToolBtn id="text" label="Text" tool={tool} setTool={setTool}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
          </ToolBtn>
          <ToolBtn id="settings" label="Settings" tool={tool} setTool={setTool}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </ToolBtn>
          {/* Studio assets + My Library — two siblings in the nav. No section
              heading; the icon/label rhythm carries the grouping on its own. */}
          <button onClick={() => setStudioMediaOpen(true)}
            className="flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent text-white/55 hover:text-white hover:bg-white/5 transition-colors"
            title="Studio media library — designer-curated assets">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/>
            </svg>
            Studio
          </button>
          {/* My Library — always visible. /dtf is auth-gated so every user
              reaching this surface is signed in; the in-browser AuthKit
              `user` can briefly be null during hydration even when the
              server cookie is valid, so don't gate the button on it. */}
          <button onClick={() => setLibraryOpen(true)}
            className="flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent text-white/55 hover:text-white hover:bg-white/5 transition-colors"
            title="My Library — your saved designs + uploaded artwork">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 12l9 4 9-4"/><path d="M3 17l9 4 9-4"/>
            </svg>
            My Library
          </button>
          <button onClick={deleteSelected} disabled={!selection}
            className={`flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 border-transparent transition-colors ${selection ? 'text-white/55 hover:text-white hover:bg-white/5' : 'text-white/20 cursor-not-allowed'}`}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            Delete
          </button>
        </nav>

        {/* CANVAS */}
        {/* LEFT SHEETS COLUMN — mirrors Designer's Locations panel. Sits between
            the tool nav and the canvas so multi-sheet management is always
            visible, not nested in the right tool sidebar. */}
        <aside className="w-[200px] flex-shrink-0 border-r border-border bg-white overflow-y-auto p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className="eyebrow !mb-0">Sheets ({sheets.length})</div>
              {dirty && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Unsaved</span>}
            </div>
            <button onClick={addSheet} title="Add sheet" className="text-gold-dark hover:text-gold text-lg font-bold">+</button>
          </div>
          {dirty && <button onClick={() => void persist()} className="text-[11px] text-gold-dark hover:underline w-full text-left">Save now</button>}
          <div className="space-y-2">
            {sheets.map((s) => (
              <div key={s.id} className={`border rounded p-2.5 ${s.id === activeId ? 'border-gold bg-gold/5' : 'border-border'}`}>
                <div className="flex items-center justify-between gap-1">
                  <button onClick={() => switchTo(s.id)} className="text-left flex-1 min-w-0">
                    <div className="text-xs font-semibold text-ink truncate">{s.name}</div>
                    <div className="text-[10px] text-ink-3">{s.lengthCm} cm</div>
                  </button>
                  <button onClick={() => duplicateSheet(s.id)} title="Duplicate" className="text-ink-3 hover:text-ink text-xs">⎘</button>
                  {sheets.length > 1 && (
                    <button onClick={() => removeSheet(s.id)} title="Delete" className="text-ink-3 hover:text-red-600 text-xs">×</button>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[11px] text-ink-3 gap-1">
                  <span>Qty</span>
                  <input type="number" min={1} value={s.qty}
                    onChange={(e) => setSheets((prev) => prev.map((x) => x.id === s.id ? { ...x, qty: Math.max(1, parseInt(e.target.value) || 1) } : x))}
                    className="w-12 px-1.5 py-0.5 bg-white border border-border rounded text-ink text-xs focus:outline-none focus:border-gold" />
                  <span className="text-ink font-semibold ml-auto">${(s.lengthCm * s.qty * dtfProduct.pricePerCm).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-2.5 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-ink-3">Total cm</span><span className="text-ink font-semibold">{totals.totalCm}</span></div>
            <div className="flex justify-between text-sm"><span>Total</span><span className="text-ink font-bold">${totals.total.toFixed(2)}</span></div>
          </div>
        </aside>

        <main className="flex-1 relative overflow-auto bg-[#EEF4FB]"
              style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 22px, rgba(120,150,200,.07) 22px 44px)' }}>
          {/* Floating "now editing" banner — top-right of the canvas pane.
              On DTF a "side" is a sheet, so the banner shows the active
              sheet name + index. Brick-on-cream matches the wizard bar. */}
          {activeSheet && (
            <div className="absolute top-3 right-3 z-20 pointer-events-none">
              <div className="bg-cream border-2 border-brick rounded shadow-lg px-3 py-2 flex items-center gap-2.5 pointer-events-auto">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brick text-white">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </span>
                <div className="leading-tight">
                  <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-brick">Now editing</div>
                  <div className="text-sm font-heading font-bold uppercase text-ink tracking-wide">
                    {activeSheet.name}
                    {sheets.length > 1 && (
                      <span className="ml-1.5 text-[10px] font-mono font-semibold text-ink-3 normal-case tracking-normal">
                        {sheets.findIndex((s) => s.id === activeSheet.id) + 1}/{sheets.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="min-h-full flex flex-col items-center justify-start py-6 px-6">
            <div className="flex items-center gap-3 mb-2 flex-wrap justify-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-3">DTF · {SHEET_WIDTH_CM} cm wide × {activeSheet.lengthCm} cm</div>
              {/* Preview-only garment-colour toggle. Doesn't affect the
                  saved spec — the press file always exports transparent.
                  Lets the customer visualise their design on different
                  substrates while they're laying it out. */}
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-ink-3" title="Preview backdrop only — the file you submit stays fully transparent so the printer can drive its white-ink underlay">
                <span>Preview on</span>
                <div className="inline-flex border border-border rounded overflow-hidden bg-white">
                  <button onClick={() => setSheetBg('white')} aria-pressed={sheetBg === 'white'}
                    className={'px-2.5 py-1 transition-colors ' + (sheetBg === 'white' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>White</button>
                  <button onClick={() => setSheetBg('grey')} aria-pressed={sheetBg === 'grey'}
                    className={'px-2.5 py-1 border-l border-border transition-colors ' + (sheetBg === 'grey' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Grey</button>
                  <button onClick={() => setSheetBg('black')} aria-pressed={sheetBg === 'black'}
                    className={'px-2.5 py-1 border-l border-border transition-colors ' + (sheetBg === 'black' ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>Black</button>
                </div>
              </div>
            </div>
            <div className="shadow-md border border-border"
                 style={{
                   width: SHEET_WIDTH_PX,
                   background: sheetBg === 'black' ? '#0C0D0F' : sheetBg === 'grey' ? '#9CA3AF' : '#ffffff',
                 }}>
              <canvas ref={canvasElRef} width={SHEET_WIDTH_PX} height={activeSheet.lengthCm * PX_PER_CM} />
            </div>
            <div className={'text-[11px] mt-3 ' + (sheetBg === 'black' ? 'text-white/70' : 'text-ink-3')}>{activeSheet.name} · {activeSheet.lengthCm} cm · qty {activeSheet.qty}</div>
          </div>
        </main>

        {/* RIGHT PANEL */}
        <aside className="w-[360px] flex-shrink-0 border-l border-border bg-white overflow-y-auto">
          {tool === 'images' && (
            <section className="p-5 space-y-5">
              <div>
                <div className="eyebrow">Upload image</div>
                <label className="block">
                  <input type="file" accept="image/*" className="hidden" onChange={onPickFile} />
                  <span className="block px-4 py-7 border-2 border-dashed border-border-2 rounded text-center text-ink-3 text-sm cursor-pointer hover:border-gold hover:text-ink transition-colors">
                    <span className="block text-2xl mb-1">⬆</span>
                    Click to upload — PNG / JPG / SVG<br/><span className="text-xs">Up to 25 MB</span>
                  </span>
                </label>
                <ul className="text-[11px] text-ink-3 mt-3 space-y-0.5 leading-snug">
                  <li>· Minimum <strong className="text-ink-2">300 DPI</strong> for print quality</li>
                  <li>· Recommended <strong className="text-ink-2">1000 × 1000 px</strong> or larger</li>
                  <li>· Transparent PNG for best DTF results</li>
                  <li>· You must own or have rights to the artwork</li>
                </ul>
                {user && <p className="text-[11px] text-ink-3 mt-2">Uploads stay in your library. Press <strong className="text-ink-2">Save</strong> at the top to save your design.</p>}
              </div>

              <InlineUploadLibrary
                ref={libraryRef}
                onUse={(a: UploadAsset) => useUpload({ id: a.id, url: a.url, originalName: a.originalName, mimeType: 'image/*', size: 0, widthPx: a.widthPx, heightPx: a.heightPx, createdAt: a.createdAt })}
                onOpenFullLibrary={() => setLibraryOpen(true)}
              />


              {selection ? (
                <div className="border-t border-border pt-4">
                  <div className="eyebrow">Selected image</div>
                  <div className="text-sm space-y-1 text-ink-2">
                    <div className="flex justify-between"><span className="text-ink-3">File</span><span className="text-ink truncate max-w-[180px]">{selection.name}</span></div>
                    <div className="flex justify-between"><span className="text-ink-3">Size on sheet</span><span className="text-ink">{selection.widthCm} × {selection.heightCm} cm</span></div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-ink-3">Print quality</span>
                      <span className="flex items-center gap-2">
                        <span className="text-ink font-semibold">{selection.ppi} DPI</span>
                        {selection.ppi != null && (() => {
                          const q = dpiQuality(selection.ppi)
                          return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${q.bg} ${q.fg}`}>{q.label}</span>
                        })()}
                      </span>
                    </div>
                    <p className="text-[11px] text-ink-3 leading-relaxed mt-2">Optimal ≥ 250 · Good 200–249 · Poor &lt; 200. Scaling up reduces effective DPI.</p>
                  </div>
                </div>
              ) : (
                <p className="text-[12px] text-ink-3">Select an image on the sheet to see its print-quality readout.</p>
              )}
            </section>
          )}

          {tool === 'text' && (
            <section className="p-5 space-y-4">
              <div className="eyebrow">Add text</div>
              <p className="text-ink-2 text-sm">Add a text layer, then use the floating toolbar above the canvas to change font, size, weight, alignment and colour.</p>
              <button onClick={addText} className="btn-gold w-full justify-center">Add text layer</button>
            </section>
          )}

          {tool === 'settings' && (
            <section className="p-5 space-y-5">
              <div>
                <div className="eyebrow">Sheet length (cm)</div>
                <input type="number" min={MIN_LENGTH_CM} max={MAX_LENGTH_CM}
                  value={activeSheet.lengthCm}
                  onChange={(e) => updateActiveSheet({ lengthCm: Math.max(MIN_LENGTH_CM, Math.min(MAX_LENGTH_CM, parseInt(e.target.value) || MIN_LENGTH_CM)) })}
                  className="input" />
                <p className="text-[11px] text-ink-3 mt-1">{MIN_LENGTH_CM}–{MAX_LENGTH_CM} cm. ${dtfProduct.pricePerCm.toFixed(2)}/cm × qty.</p>
              </div>
              <div>
                <div className="eyebrow">Quantity (this sheet)</div>
                <input type="number" min={1} value={activeSheet.qty}
                  onChange={(e) => updateActiveSheet({ qty: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="eyebrow">Margin (cm)</div>
                  <input type="number" min={0} step="0.5" value={marginCm} onChange={(e) => setMarginCm(Math.max(0, parseFloat(e.target.value) || 0))} className="input" />
                </div>
                <div>
                  <div className="eyebrow">Padding (cm)</div>
                  <input type="number" min={0} step="0.5" value={paddingCm} onChange={(e) => setPaddingCm(Math.max(0, parseFloat(e.target.value) || 0))} className="input" />
                </div>
              </div>
              <p className="text-[11px] text-ink-3">Margin = empty space around the sheet edge. Padding = guide spacing between images (visual only — actual press position uses your placement).</p>
            </section>
          )}

        </aside>
      </div>

      <TextToolbar canvas={fabricRef.current} tick={canvasTick} />
      <AlignmentToolbar
        canvas={fabricRef.current}
        tick={canvasTick}
        bounds={{ left: 0, top: 0, width: SHEET_WIDTH_PX, height: activeSheet.lengthCm * PX_PER_CM }}
        sizeInfo={selection && selection.widthCm != null && selection.heightCm != null && selection.ppi != null ? {
          widthMm:  Math.round(selection.widthCm  * 10),
          heightMm: Math.round(selection.heightCm * 10),
          ppi:      selection.ppi,
        } : null}
        stackAboveTextToolbar={(() => {
          const a = fabricRef.current?.getActiveObject()
          return !!(a && (a.type === 'i-text' || a.type === 'textbox' || a.type === 'text'))
        })()}
      />
      <UploadConsentModal
        open={!!pendingUpload}
        pending={pendingUpload}
        warnings={pendingWarnings}
        onConfirm={() => {
          if (pendingUpload) { rememberSessionConsent(); void doUpload(pendingUpload) }
          setPendingUpload(null); setPendingWarnings([])
        }}
        onCancel={() => { setPendingUpload(null); setPendingWarnings([]) }}
      />
      <StudioMediaPicker
        open={studioMediaOpen}
        onClose={() => setStudioMediaOpen(false)}
        onPick={(a: StudioAsset) => useUpload({ id: a.id, originalName: a.originalName, url: a.url, mimeType: 'image/*', size: 0, widthPx: a.widthPx, heightPx: a.heightPx, createdAt: a.createdAt })}
      />
      <LibraryDrawer
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        kind="dtf"
        onOpenDesign={openSavedDesign}
        onUseUpload={useUpload}
      />

      {/* SUBMIT MODAL */}
      {submit.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-ink font-heading uppercase font-bold text-lg">Place your DTF order</h3>
                <p className="text-xs text-ink-3">Review the proof + shipping, then we'll press and ship.</p>
              </div>
              <button onClick={() => setSubmit({ open: false, busy: false })} className="text-ink-3 hover:text-ink text-2xl leading-none">×</button>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-6">
              <div>
                <div className="border border-border rounded overflow-hidden bg-surface">
                  {submit.proof && <img src={submit.proof} alt="Proof — active sheet" className="w-full block" />}
                </div>
                <p className="text-[11px] text-ink-3 mt-2">Shown: current sheet ({activeSheet.name}). All sheets in your design are submitted — production reviews each one against the saved spec before pressing.</p>
                <div className="mt-4 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-ink-3">Sheets</span><span className="text-ink font-semibold">{sheets.length}</span></div>
                  <div className="flex justify-between"><span className="text-ink-3">Total length</span><span className="text-ink font-semibold">{totals.totalCm} cm</span></div>
                  <div className="flex justify-between"><span className="text-ink-3">Rate</span><span className="text-ink font-semibold">${dtfProduct.pricePerCm.toFixed(2)}/cm</span></div>
                  <div className="flex justify-between text-base"><span className="text-ink-3">Total</span><span className="text-ink font-bold">${totals.total.toFixed(2)}</span></div>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="eyebrow">Shipping</div>
                <input className="input" placeholder="Full name" value={shipping.fullName} onChange={(e) => setShipping({ ...shipping, fullName: e.target.value })} />
                <input className="input" placeholder="Email" type="email" value={shipping.email} onChange={(e) => setShipping({ ...shipping, email: e.target.value })} />
                <input className="input" placeholder="Phone (optional)" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} />
                <input className="input" placeholder="Street address" value={shipping.line1} onChange={(e) => setShipping({ ...shipping, line1: e.target.value })} />
                <input className="input" placeholder="Apartment, suite, etc. (optional)" value={shipping.line2} onChange={(e) => setShipping({ ...shipping, line2: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <input className="input" placeholder="Suburb" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} />
                  <input className="input" placeholder="State" value={shipping.region} onChange={(e) => setShipping({ ...shipping, region: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input className="input" placeholder="Postcode" value={shipping.postcode} onChange={(e) => setShipping({ ...shipping, postcode: e.target.value })} />
                  <input className="input" placeholder="Country" value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value.toUpperCase().slice(0, 2) })} />
                </div>
                <button onClick={placeOrder} disabled={submit.busy} className="btn-gold w-full justify-center mt-2">
                  {submit.busy ? 'Submitting…' : 'Place order'}
                </button>
                <p className="text-[11px] text-ink-3 leading-relaxed">Payment integration is coming — for now the order is placed as <strong>pending payment</strong> and EVRST Studio will follow up with an invoice. {!user && <>Sign in next time to track orders in one place.</>}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────── Wizard step popup ───────────────────
          Same brick-on-cream modal Designer uses — focused inputs
          for whichever step in the EditorWizardBar the user clicked. */}
      <WizardStepModal
        open={wizModalStep === 'upload'}
        stepNumber={1} totalSteps={5}
        title="Add your artwork"
        subtitle="Upload one image per design. You can drop multiples onto the sheet."
        onClose={() => setWizModalStep(null)}
        onNext={() => gotoWizStep('next')}
        nextDisabled={!hasArtwork}>
        <div className="space-y-4">
          <label className="block">
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onPickFile(e); setWizModalStep(null) }} />
            <span className="block px-4 py-8 border-2 border-dashed border-brick/40 bg-white rounded text-center text-ink-3 text-sm cursor-pointer hover:border-brick hover:text-ink transition-colors">
              <span className="block text-2xl mb-1">⬆</span>
              Click to upload — PNG, JPG, or SVG up to 10 MB
            </span>
          </label>
          <button onClick={() => { setLibraryOpen(true); setWizModalStep(null) }}
            className="w-full px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick">
            Open My Library
          </button>
          <ul className="text-[11px] text-ink-3 space-y-0.5 leading-snug pt-1">
            <li>· Minimum <strong className="text-ink-2">300 DPI</strong> for print quality</li>
            <li>· Transparent PNG works best — the DTF sheet stays clear</li>
            <li>· Drag each placed image to position it on the 57 cm sheet</li>
          </ul>
        </div>
      </WizardStepModal>

      <WizardStepModal
        open={wizModalStep === 'placement'}
        stepNumber={2} totalSteps={5}
        title="Artwork placement"
        subtitle="Position your designs on the 57 cm sheet. Drag and resize on the canvas, or use the quick actions below."
        onClose={() => setWizModalStep(null)}
        onPrev={() => gotoWizStep('prev')}
        onNext={() => gotoWizStep('next')}
        nextDisabled={!hasArtwork}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={dtfPlacementCenter} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/>
              </svg>
              Center on sheet
            </button>
            <button onClick={() => dtfPlacementFit('contain')} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="1"/><polyline points="8 12 12 8 16 12"/><polyline points="8 16 12 20 16 16"/>
              </svg>
              Fit inside
            </button>
            <button onClick={() => dtfPlacementFit('width')} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"/><polyline points="6 9 3 12 6 15"/><polyline points="18 9 21 12 18 15"/>
              </svg>
              Fit to 57 cm width
            </button>
            <button onClick={() => dtfPlacementFit('height')} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="3" x2="12" y2="21"/><polyline points="9 6 12 3 15 6"/><polyline points="9 18 12 21 15 18"/>
              </svg>
              Fit to sheet length
            </button>
            <button onClick={() => dtfPlacementScale(0.9)} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              − Smaller
            </button>
            <button onClick={() => dtfPlacementScale(1.1)} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              + Bigger
            </button>
          </div>
          {!hasArtwork && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              You need to add artwork first — go back to step 1.
            </div>
          )}
          <p className="text-[11px] text-ink-3 leading-relaxed">
            <strong className="text-ink-2">Tip:</strong> Each image you drop on the sheet shows its live DPI in the toolbar — anything under 300 DPI will print blurry, so use Fit inside / Smaller if a piece looks fuzzy.
          </p>
        </div>
      </WizardStepModal>

      <WizardStepModal
        open={wizModalStep === 'sheet'}
        stepNumber={3} totalSteps={5}
        title="Sheet length"
        subtitle={`Roll length for the active sheet (cm). Range ${MIN_LENGTH_CM} – ${MAX_LENGTH_CM}.`}
        onClose={() => setWizModalStep(null)}
        onPrev={() => gotoWizStep('prev')}
        onNext={() => gotoWizStep('next')}
        nextDisabled={!(activeSheet.lengthCm >= MIN_LENGTH_CM && activeSheet.lengthCm <= MAX_LENGTH_CM)}>
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1">Length (cm)</label>
              <input type="number" min={MIN_LENGTH_CM} max={MAX_LENGTH_CM} value={activeSheet.lengthCm}
                onChange={(e) => {
                  const n = Math.max(MIN_LENGTH_CM, Math.min(MAX_LENGTH_CM, parseInt(e.target.value) || MIN_LENGTH_CM))
                  updateActiveSheet({ lengthCm: n })
                }}
                className="input !text-lg !py-3 font-mono" />
            </div>
            <div className="text-xs text-ink-3 pb-3">
              Width is fixed at <strong className="text-ink">57 cm</strong>.
            </div>
          </div>
          <input type="range" min={MIN_LENGTH_CM} max={MAX_LENGTH_CM} step={5} value={activeSheet.lengthCm}
            onChange={(e) => updateActiveSheet({ lengthCm: parseInt(e.target.value) })}
            className="w-full accent-brick" />
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            <span>{MIN_LENGTH_CM} cm</span>
            <span>{MAX_LENGTH_CM} cm</span>
          </div>
        </div>
      </WizardStepModal>

      <WizardStepModal
        open={wizModalStep === 'qty'}
        stepNumber={4} totalSteps={5}
        title="Quantity per sheet"
        subtitle="How many prints of each sheet do you need?"
        onClose={() => setWizModalStep(null)}
        onPrev={() => gotoWizStep('prev')}
        onNext={() => gotoWizStep('next')}
        nextLabel={`Submit ($${totals.total.toFixed(2)}) →`}
        nextDisabled={!sheets.some(s => s.qty > 0)}>
        <div className="space-y-2">
          {sheets.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-3 text-sm rounded px-3 py-2.5 bg-white border border-border">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-ink-3 font-bold">Sheet {idx + 1}</div>
                <div className="text-ink font-semibold truncate">{s.name} <span className="text-ink-3 font-normal">· {s.lengthCm} cm</span></div>
              </div>
              <input type="number" min={0} value={s.qty}
                onChange={(e) => {
                  const n = Math.max(0, parseInt(e.target.value) || 0)
                  setSheets((prev) => prev.map((x) => x.id === s.id ? { ...x, qty: n } : x))
                }}
                className="w-20 px-2 py-1.5 bg-white border border-border rounded text-ink text-sm focus:outline-none focus:border-brick" />
            </div>
          ))}
          <div className="border-t border-brick/30 mt-3 pt-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-ink-3">Sheets</span><span className="text-ink font-semibold">{sheets.length}</span></div>
            <div className="flex justify-between text-base"><span className="text-ink-2 font-semibold">Total</span><span className="text-brick font-bold">${totals.total.toFixed(2)}</span></div>
          </div>
        </div>
      </WizardStepModal>
    </div>
  )
}

function ToolBtn({ id, label, tool, setTool, children }: { id: Tool; label: string; tool: Tool; setTool: (t: Tool) => void; children: React.ReactNode }) {
  const active = tool === id
  return (
    <button onClick={() => setTool(id)}
      className={`flex flex-col items-center justify-center gap-1.5 w-full py-3 px-2 text-[10px] uppercase tracking-wider font-semibold border-l-2 transition-colors ${active ? 'text-gold border-gold bg-white/5' : 'text-white/55 border-transparent hover:text-white hover:bg-white/5'}`}>
      {children}
      {label}
    </button>
  )
}
